import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShoppingBag, X, MapPin, CheckCircle, CircleNotch, ChatCircle, Star, Sparkle, Package, Truck, Storefront as StorefrontIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import MultiImageUploader from "@/components/MultiImageUploader";
import ImageGallery from "@/components/ImageGallery";
import { Link } from "react-router-dom";

import { useLang } from "@/context/LangContext";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80";
const CATEGORIES = ["Electronics", "Furniture", "Vehicles", "Books", "Clothing", "Groceries", "Home", "Beauty", "Other"];

const TABS_META = [
  { v: "", labelKey: "market.tab.all", icon: ShoppingBag },
  { v: "fresh", labelKey: "market.tab.fresh", icon: Sparkle },
  { v: "second-hand", labelKey: "market.tab.second", icon: Package },
];

export default function Marketplace() {
  const { user } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSellerProfile, setShowSellerProfile] = useState(false);
  const [buying, setBuying] = useState(null);
  const [fxRate, setFxRate] = useState(83);
  const [featureBusy, setFeatureBusy] = useState(null);
  const [codItem, setCodItem] = useState(null);
  const [enquiryItem, setEnquiryItem] = useState(null);
  const [sort, setSort] = useState("recent");

  const params = useMemo(() => {
    const p = {}; if (category) p.category = category; if (type) p.condition_type = type; if (sort === "rating") p.sort = "rating"; return p;
  }, [category, type, sort]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketplace/listings", { params }); setItems(data); }
    catch { toast.error("Failed to load listings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  useEffect(() => {
    api.get("/fx/usd-inr").then(({ data }) => setFxRate(data.rate)).catch(() => {});
  }, []);

  const featureListing = async (item) => {
    setFeatureBusy(item.id);
    try {
      const { data } = await api.post("/marketplace/listings/feature", { listing_id: item.id });
      toast.success(`Featured for 7 days — ${data.credits_spent} credits spent`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not feature"); }
    finally { setFeatureBusy(null); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D49A36]">{t("nav.marketplace")}</div>
          <h1 className="font-heading text-5xl mt-2 tracking-tight">{t("market.title.a")} <em className="text-[#D49A36] not-italic">{t("market.title.b")}</em> {t("market.title.c")} <em className="text-[#D49A36] not-italic">{t("market.title.d")}</em>.</h1>
          <p className="text-[#5C564D] mt-2 max-w-2xl">{t("market.subtitle")}</p>
        </div>
        {user && user !== false && (
          <div className="flex gap-2 flex-wrap">
            <button data-testid="btn-seller-profile" onClick={() => setShowSellerProfile(true)} className="samaaj-btn-outline">Seller profile</button>
            <button data-testid="btn-sell-item" onClick={() => setShowForm(true)} className="samaaj-btn-primary">Sell something</button>
          </div>
        )}
      </div>

      {/* Fresh / Second-hand toggle */}
      <div className="mt-8 inline-flex p-1 rounded-full bg-[#F2EBE5] border border-[#E5D9CC]" data-testid="market-type-tabs">
        {TABS_META.map((tt) => {
          const Icon = tt.icon;
          const active = type === tt.v;
          return (
            <button
              key={tt.labelKey}
              data-testid={`market-tab-${tt.v || "all"}`}
              onClick={() => setType(tt.v)}
              className="px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5"
              style={{
                background: active ? "#D95A41" : "transparent",
                color: active ? "#F9F6F0" : "#5C564D",
                transition: "background-color 180ms ease-out, color 180ms ease-out",
              }}
            >
              <Icon size={14} weight={active ? "fill" : "regular"} /> {t(tt.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Category chips */}
      <div className="mt-4 flex flex-wrap gap-2 items-center" data-testid="market-categories">
        <button onClick={() => setCategory("")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${!category ? "bg-[#2C4A3B] text-[#F9F6F0]" : "bg-[#F2EBE5] text-[#2C4A3B]"}`}>All categories</button>
        {CATEGORIES.map((c) => (
          <button key={c} data-testid={`cat-${c}`} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${category === c ? "bg-[#2C4A3B] text-[#F9F6F0]" : "bg-[#F2EBE5] text-[#2C4A3B]"}`} style={{ transition: "background-color 180ms ease-out" }}>{c}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-[#5C564D]">Sort</label>
          <select data-testid="market-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="px-2 py-1 rounded-full text-xs bg-[#F2EBE5] border border-[#E5D9CC]">
            <option value="recent">Recent</option>
            <option value="rating">Top-rated sellers</option>
          </select>
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-[#5C564D]">Loading listings…</div>
        ) : items.length === 0 ? (
          <div className="samaaj-card p-10 text-center">
            <ShoppingBag size={32} className="mx-auto text-[#D49A36]" />
            <div className="font-heading text-2xl mt-3">No listings yet</div>
            <p className="text-[#5C564D] mt-2">Be the first to {type === "fresh" ? "sell a fresh product" : type === "second-hand" ? "list a used item" : "list something"} in this section.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="market-grid">
            {items.map((item) => {
              const isMine = user && user !== false && item.seller_id === user.id;
              const isFresh = item.condition_type === "fresh";
              return (
                <div key={item.id} className="samaaj-card overflow-hidden relative" data-testid={`item-${item.id}`}>
                  {item.is_featured && (
                    <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[10px] font-bold text-[#F9F6F0] flex items-center gap-1" style={{ background: "#D95A41" }}>
                      <Star size={10} weight="fill" /> Featured
                    </div>
                  )}
                  <div className="aspect-[4/3] relative">
                    <ImageGallery images={item.images} fallback={item.image_url || DEFAULT_IMG} alt={item.title} />
                    {item.status === "sold" && (
                      <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold text-[#F9F6F0] bg-[#2C4A3B] flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Sold</div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        data-testid={`type-${item.id}`}
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase flex items-center gap-1"
                        style={{
                          background: isFresh ? "#DDEBDF" : "#F2EBE5",
                          color: isFresh ? "#2C4A3B" : "#5C564D",
                        }}
                      >
                        {isFresh ? <><Sparkle size={10} weight="fill" /> Fresh</> : <><Package size={10} /> Second-hand</>}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-[#F9F6F0]" style={{ background: "#D49A36" }}>{item.category}</span>
                    </div>
                    <div className="font-heading text-xl mt-2 text-[#1C1B1A]">{item.title}</div>
                    <div className="text-xs text-[#5C564D] flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1"><MapPin size={12} /> {item.location}</span>
                      <span>{item.condition}</span>
                    </div>
                    <p className="text-sm text-[#5C564D] mt-2 line-clamp-2">{item.description}</p>
                    <div className="mt-4 flex items-end justify-between gap-2">
                      <div>
                        <div className="font-heading text-2xl text-[#D95A41]">${(+item.price).toFixed(2)}</div>
                        {item.price_inr && <div className="text-xs text-[#5C564D]">≈ ₹{Math.round((item.price ?? 0) * fxRate)}</div>}
                      </div>
                      <div className="flex flex-col gap-1.5 items-stretch">
                        {isMine ? (
                          <>
                            <button
                              data-testid={`btn-feature-${item.id}`}
                              disabled={item.is_featured || featureBusy === item.id}
                              onClick={() => featureListing(item)}
                              className={item.is_featured ? "samaaj-btn-outline text-xs" : "samaaj-btn-primary text-xs"}
                              style={{ opacity: item.is_featured ? 0.7 : 1 }}
                            >
                              {featureBusy === item.id ? <CircleNotch className="inline animate-spin" size={12} /> : <Star size={12} weight={item.is_featured ? "regular" : "fill"} className="inline mr-1" />}
                              {item.is_featured ? "Featured" : "Feature (200 credits)"}
                            </button>
                          </>
                        ) : (
                          <>
                            {item.status === "available" ? (
                              <>
                                <button
                                  data-testid={`btn-cod-${item.id}`}
                                  onClick={() => setCodItem(item)}
                                  className="samaaj-btn-primary text-sm flex items-center gap-1.5 justify-center"
                                >
                                  <Truck size={12} weight="fill" /> Reserve · pay at delivery
                                </button>
                                <button
                                  data-testid={`btn-enquiry-${item.id}`}
                                  onClick={() => setEnquiryItem(item)}
                                  className="samaaj-btn-outline text-xs flex items-center gap-1.5 justify-center"
                                >
                                  <ChatCircle size={12} weight="fill" /> Send enquiry
                                </button>
                              </>
                            ) : (
                              <button disabled className="samaaj-btn-outline text-sm flex items-center gap-1.5 justify-center opacity-60">
                                {item.status === "sold" ? t("market.sold") : t("market.reserved")}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-[#5C564D] flex items-center flex-wrap gap-x-1 gap-y-1">
                      <span>By</span>
                      <Link to={`/store/${item.seller_id}`} data-testid={`link-store-${item.id}`} className="font-semibold text-[#2C4A3B] hover:underline inline-flex items-center gap-1"><StorefrontIcon size={11} weight="duotone" /> {item.seller_name}</Link>
                      <VerifiedSellerBadge sellerId={item.seller_id} testid={`vb-${item.id}`} />
                      {isMine ? <span>(you)</span> : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && <SellModal onClose={() => setShowForm(false)} onCreated={(x) => { setItems([x, ...items]); setShowForm(false); }} />}
      {showSellerProfile && (
        <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
          <div className="max-w-3xl w-full my-6" data-testid="seller-profile-modal">
            <div className="flex justify-end mb-2"><button onClick={() => setShowSellerProfile(false)} className="rounded-full bg-white p-2 shadow"><X size={18} /></button></div>
            <SellerProfilePanel />
          </div>
        </div>
      )}
      {codItem && <CODModal item={codItem} onClose={() => setCodItem(null)} onDone={() => { setCodItem(null); load(); }} />}
      {enquiryItem && <EnquiryModal item={enquiryItem} onClose={() => setEnquiryItem(null)} onDone={() => setEnquiryItem(null)} />}
    </div>
  );
}


function EnquiryModal({ item, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error("Type a message for the seller"); return; }
    setBusy(true);
    try {
      await api.post("/marketplace/enquiries", { listing_id: item.id, message });
      toast.success("Enquiry sent to seller");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Enquiry failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4">
      <div className="samaaj-card max-w-md w-full p-6 bg-[#F9F6F0]">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#2C4A3B]">Ask the seller</div>
            <div className="font-heading text-2xl mt-1">{item.title}</div>
            <div className="text-xs text-[#5C564D]">Seller: {item.seller_name || "Community member"}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <textarea
            data-testid="enquiry-message"
            required rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Say hi, ask about condition, model, availability, delivery timing…"
            className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]"
          />
          <p className="text-[11px] text-[#5C564D]">The seller will see your enquiry in the “Enquiries” section. Replies come as notifications.</p>
          <button data-testid="enquiry-submit" disabled={busy} className="samaaj-btn-primary w-full">
            {busy ? "Sending…" : "Send enquiry"}
          </button>
        </form>
      </div>
    </div>
  );
}


function CODModal({ item, onClose, onDone }) {
  const [form, setForm] = useState({ delivery_name: "", delivery_phone: "", delivery_address: "", delivery_pincode: "", buyer_note: "", payment_method: "cod" });
  const [seller, setSeller] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item?.seller_id) api.get(`/sellers/${item.seller_id}/public`).then(({ data }) => setSeller(data)).catch((err) => console.error(err));
  }, [item?.seller_id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (form.delivery_pincode && !/^\d{6}$/.test(form.delivery_pincode.trim())) {
      toast.error("PIN code must be 6 digits"); return;
    }
    setBusy(true);
    try {
      await api.post("/marketplace/orders", {
        listing_id: item.id, quantity: 1, payment_method: form.payment_method,
        delivery_name: form.delivery_name, delivery_phone: form.delivery_phone,
        delivery_address: form.delivery_address, delivery_pincode: form.delivery_pincode || null,
        buyer_note: form.buyer_note,
      });
      toast.success("Request sent — seller will respond shortly");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Request failed"); }
    finally { setBusy(false); }
  };
  const method = form.payment_method;
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-md w-full p-6 bg-[#F9F6F0] my-6" data-testid="cod-modal">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#2C4A3B]">Request this product</div>
            <div className="font-heading text-2xl mt-1 truncate">{item.title}</div>
            <div className="text-sm text-[#5C564D]">Seller price: ${(+item.price).toFixed(2)}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]" data-testid="cod-close"><X size={18} /></button>
        </div>

        <div className="mt-3 p-3 rounded-xl text-[11px]" style={{ background: "#FFF4D8", borderLeft: "3px solid #D49A36", color: "#6B5213" }} data-testid="direct-payment-notice">
          Payments are made <strong>directly between buyer and seller</strong>. Anveshan does not collect, hold, process or settle payments.
        </div>

        <div className="mt-4">
          <div className="text-xs font-semibold text-[#5C564D] mb-1">Payment method</div>
          <div className="flex flex-wrap gap-1.5" data-testid="pay-methods">
            {[
              { v: "cod", label: "Cash on Delivery", avail: seller?.accepts_cod !== false },
              { v: "upi", label: "UPI (direct to seller)", avail: !!seller?.upi_id },
              { v: "bank_transfer", label: "Bank transfer", avail: !!seller?.accepts_bank_transfer },
              { v: "contact_seller", label: "Contact seller", avail: true },
            ].map((o) => (
              <button key={o.v} type="button" disabled={!o.avail} onClick={() => set("payment_method", o.v)} data-testid={`pm-${o.v}`}
                className="px-3 py-1 rounded-full text-xs font-semibold border disabled:opacity-40"
                style={{ background: method === o.v ? "#2C4A3B" : "white", color: method === o.v ? "white" : "#2C4A3B", borderColor: "#2C4A3B" }}>
                {o.label}{!o.avail ? " (unavailable)" : ""}
              </button>
            ))}
          </div>
        </div>

        {(method === "upi" || method === "bank_transfer") && seller && (
          <div className="mt-3 p-3 rounded-xl bg-white border border-[#E5D9CC]" data-testid="seller-pay-info">
            <div className="text-xs font-semibold text-[#2C4A3B]">Seller’s direct payment details</div>
            {method === "upi" ? (
              <>
                {seller.upi_id && <div className="mt-1 text-sm font-mono">UPI: <span className="select-all">{seller.upi_id}</span></div>}
                {seller.payment_qr_url && <img src={seller.payment_qr_url} alt="Seller UPI QR" className="mt-2 max-h-48 rounded-xl border border-[#E5D9CC]" data-testid="seller-qr" />}
              </>
            ) : (
              <>
                {seller.bank_account_last4 && <div className="mt-1 text-sm font-mono">A/C ••••{seller.bank_account_last4}</div>}
                {seller.bank_ifsc && <div className="text-sm font-mono">IFSC: {seller.bank_ifsc}</div>}
              </>
            )}
            {seller.payment_notes && <div className="mt-1 text-[11px] text-[#5C564D]">{seller.payment_notes}</div>}
            <div className="mt-2 text-[11px] text-[#5C564D]">Please pay the seller directly and keep your transaction reference. Anveshan does not verify this payment.</div>
          </div>
        )}

        <form onSubmit={submit} className="mt-4 space-y-2">
          <input data-testid="cod-name" required placeholder="Recipient name" value={form.delivery_name} onChange={(e) => set("delivery_name", e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
          <input data-testid="cod-phone" required placeholder="Phone number" value={form.delivery_phone} onChange={(e) => set("delivery_phone", e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
          <textarea data-testid="cod-address" required={method === "cod"} rows={2} placeholder="Delivery address" value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
          <input data-testid="cod-pin" placeholder="PIN code (6 digits, optional)" value={form.delivery_pincode} onChange={(e) => set("delivery_pincode", e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
          <textarea data-testid="cod-note" rows={2} placeholder="Note to seller (optional)" value={form.buyer_note} onChange={(e) => set("buyer_note", e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
          <button data-testid="cod-submit" disabled={busy} className="samaaj-btn-primary w-full">{busy ? "Sending…" : "Send request to seller"}</button>
        </form>
      </div>
    </div>
  );
}

function SellModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", category: "Electronics", price: 20, condition: "New", location: "", description: "", images: [], condition_type: "fresh", model_no: "", delivery_mode: "self-pickup", delivery_charges: 0, delivery_charges_by: "buyer" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...f, price: Number(f.price), delivery_charges: Number(f.delivery_charges) || 0, image_url: f.images[0] || "" };
      const { data } = await api.post("/marketplace/listings", payload);
      toast.success("Listing published");
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const chooseType = (v) => setF((s) => ({ ...s, condition_type: v, condition: v === "fresh" ? (s.condition === "Like new" ? "New" : s.condition || "New") : "Like new" }));
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-xl w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">List an item</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>

        <div className="mt-4">
          <div className="text-xs font-semibold text-[#5C564D] mb-2">What are you selling?</div>
          <div className="grid grid-cols-2 gap-2" data-testid="sell-type">
            <button type="button" data-testid="sell-type-fresh" onClick={() => chooseType("fresh")}
              className="p-3 rounded-xl text-left"
              style={{
                background: f.condition_type === "fresh" ? "#DDEBDF" : "#F2EBE5",
                border: `1.5px solid ${f.condition_type === "fresh" ? "#2C4A3B" : "#E5D9CC"}`,
                transition: "background-color 180ms ease-out, border-color 180ms ease-out",
              }}>
              <div className="flex items-center gap-2 font-semibold text-[#1C1B1A]"><Sparkle size={14} weight="fill" className="text-[#2C4A3B]" /> Fresh product</div>
              <div className="text-xs text-[#5C564D] mt-1">Brand-new stock (groceries, clothes, hand-made goods).</div>
            </button>
            <button type="button" data-testid="sell-type-second" onClick={() => chooseType("second-hand")}
              className="p-3 rounded-xl text-left"
              style={{
                background: f.condition_type === "second-hand" ? "#EFE4D3" : "#F2EBE5",
                border: `1.5px solid ${f.condition_type === "second-hand" ? "#D49A36" : "#E5D9CC"}`,
                transition: "background-color 180ms ease-out, border-color 180ms ease-out",
              }}>
              <div className="flex items-center gap-2 font-semibold text-[#1C1B1A]"><Package size={14} className="text-[#D49A36]" /> Second-hand</div>
              <div className="text-xs text-[#5C564D] mt-1">Used items in good condition (electronics, furniture).</div>
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3">
          <input data-testid="sell-title" required placeholder="Item title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <select data-testid="sell-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input data-testid="sell-price" required type="number" step="0.01" placeholder="Price (USD)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="sell-condition" required placeholder={f.condition_type === "fresh" ? "e.g. New / Sealed / Homemade" : "e.g. Like new / Good / Fair"} value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="sell-location" required placeholder="Location where you'd like to sell" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="sell-model" placeholder="Model number (optional)" value={f.model_no} onChange={(e) => setF({ ...f, model_no: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <select data-testid="sell-delivery-mode" value={f.delivery_mode} onChange={(e) => setF({ ...f, delivery_mode: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
            <option value="self-pickup">Self pickup</option>
            <option value="hand-delivery">Hand delivery</option>
            <option value="courier">Courier</option>
          </select>
          <select data-testid="sell-delivery-by" value={f.delivery_charges_by} onChange={(e) => setF({ ...f, delivery_charges_by: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
            <option value="buyer">Delivery charge · buyer pays</option>
            <option value="seller">Delivery charge · seller covers</option>
            <option value="split">Delivery charge · split 50/50</option>
          </select>
          <input data-testid="sell-delivery-charges" type="number" step="0.01" placeholder="Delivery charges (USD, 0 if none)" value={f.delivery_charges} onChange={(e) => setF({ ...f, delivery_charges: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <div className="col-span-2 text-[11px] text-[#5C564D] bg-[#F2EBE5] rounded-xl p-2 border border-[#E5D9CC]">
            All Anveshan listings settle as <b>Cash on Delivery</b>. No online payment is collected — the buyer pays the seller directly on hand-off.
          </div>
          <div className="col-span-2">
            <MultiImageUploader testid="sell-images" label="Item photos" max={6} value={f.images} onChange={(urls) => setF({ ...f, images: urls })} />
          </div>
          <textarea data-testid="sell-description" required rows={4} placeholder="Describe the item honestly" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <button data-testid="sell-submit" disabled={busy} className="samaaj-btn-primary col-span-2">{busy ? "Publishing…" : "Publish listing"}</button>
        </form>
      </div>
    </div>
  );
}


// Cached verified-seller lookup — fetches /sellers/{id}/public once per id.
const _sellerCache = new Map();
function VerifiedSellerBadge({ sellerId, testid }) {
  const [v, setV] = useState(_sellerCache.get(sellerId) ?? null);
  useEffect(() => {
    if (!sellerId || _sellerCache.has(sellerId)) return;
    api.get(`/sellers/${sellerId}/public`).then(({ data }) => {
      const verified = data?.verification_status === "verified";
      _sellerCache.set(sellerId, verified); setV(verified);
    }).catch(() => { _sellerCache.set(sellerId, false); setV(false); });
  }, [sellerId]);
  if (!v) return null;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ background: "#DDEBDF", color: "#2C4A3B" }} title="Verified seller">
      <CheckCircle size={10} weight="fill" /> Verified
    </span>
  );
}

export function SellerProfilePanel() {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/sellers/profile/mine").then(({ data }) => {
      setP(data || { seller_type: "individual", accepts_cod: true, accepts_bank_transfer: false });
    }).catch(() => setP({ seller_type: "individual", accepts_cod: true })).finally(() => setLoading(false));
  }, []);
  if (loading || !p) return <div className="samaaj-card p-6 text-sm text-[#5C564D]">Loading seller profile…</div>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const mask4 = (s) => (s ? String(s).replace(/\s+/g, "").slice(-4) : "");
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...p,
        pan_last4: mask4(p.pan_last4),
        gstin_last4: mask4(p.gstin_last4),
        bank_account_last4: mask4(p.bank_account_last4),
      };
      const { data } = await api.put("/sellers/profile", payload); setP(data);
      toast.success(data.verification_status === "verified" ? "Saved" : "Saved — awaiting admin verification");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const statusColor = { verified: "#15803D", pending: "#D97706", under_review: "#D97706", rejected: "#B91C1C", suspended: "#B91C1C" }[p.verification_status] || "#5C564D";
  return (
    <form onSubmit={save} className="samaaj-card p-6 space-y-4" data-testid="seller-profile-form">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs tracking-widest uppercase font-bold text-[#2C4A3B]">Seller profile</div>
          <div className="font-heading text-2xl">Verify once, publish freely.</div>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: `${statusColor}14`, color: statusColor }} data-testid="seller-status">
          {p.verification_status || "pending"}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Seller type</div>
          <select value={p.seller_type} onChange={(e) => set("seller_type", e.target.value)} data-testid="sp-type" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm">
            <option value="individual">Individual</option>
            <option value="business">Business</option>
            <option value="company">Company / Organisation</option>
          </select>
        </label>
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Legal name / Full name</div>
          <input value={p.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} data-testid="sp-legal" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Business / trade name</div>
          <input value={p.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="sp-biz" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Business address</div>
          <input value={p.business_address || ""} onChange={(e) => set("business_address", e.target.value)} data-testid="sp-addr" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Pickup location</div>
          <input value={p.pickup_location || ""} onChange={(e) => set("pickup_location", e.target.value)} data-testid="sp-pickup" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Category</div>
          <input value={p.business_category || ""} onChange={(e) => set("business_category", e.target.value)} data-testid="sp-cat" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Contact phone</div>
          <input value={p.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="sp-phone" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
        <label className="block text-xs"><div className="text-[#5C564D] mb-1">Contact email</div>
          <input value={p.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="sp-email" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
      </div>
      <div className="rounded-xl p-3" style={{ background: "#FFF4D8", color: "#6B5213" }}>
        <div className="text-xs font-bold uppercase tracking-widest">Identity verification (optional)</div>
        <p className="text-[11px] mt-1">We store only the LAST 4 digits. Please never share scans or full numbers. GST only if your business is registered.</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-2">
          <label className="block text-xs"><div className="mb-1">PAN last 4</div>
            <input value={p.pan_last4 || ""} onChange={(e) => set("pan_last4", e.target.value)} data-testid="sp-pan" maxLength={4} placeholder="••••" className="w-full px-3 py-2 rounded-xl border border-[#E6CF91] text-sm" /></label>
          <label className="block text-xs"><div className="mb-1">GSTIN last 4</div>
            <input value={p.gstin_last4 || ""} onChange={(e) => set("gstin_last4", e.target.value)} data-testid="sp-gstin" maxLength={4} placeholder="••••" className="w-full px-3 py-2 rounded-xl border border-[#E6CF91] text-sm" /></label>
          <label className="block text-xs"><div className="mb-1">Alt. ID note</div>
            <input value={p.aadhaar_alt_proof || ""} onChange={(e) => set("aadhaar_alt_proof", e.target.value)} data-testid="sp-alt" placeholder="e.g. Passport ••••1234" className="w-full px-3 py-2 rounded-xl border border-[#E6CF91] text-sm" /></label>
        </div>
      </div>
      <div>
        <div className="text-xs tracking-widest uppercase font-bold text-[#2C4A3B]">Direct-payment info (shown to buyers)</div>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Your UPI ID</div>
            <input value={p.upi_id || ""} onChange={(e) => set("upi_id", e.target.value)} data-testid="sp-upi" placeholder="yourname@upi" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Payment QR image URL</div>
            <input value={p.payment_qr_url || ""} onChange={(e) => set("payment_qr_url", e.target.value)} data-testid="sp-qr" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Bank A/C last 4</div>
            <input value={p.bank_account_last4 || ""} onChange={(e) => set("bank_account_last4", e.target.value)} data-testid="sp-bank" maxLength={4} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">IFSC</div>
            <input value={p.bank_ifsc || ""} onChange={(e) => set("bank_ifsc", e.target.value)} data-testid="sp-ifsc" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!p.accepts_cod} onChange={(e) => set("accepts_cod", e.target.checked)} data-testid="sp-cod" /> Accept Cash on Delivery</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!p.accepts_bank_transfer} onChange={(e) => set("accepts_bank_transfer", e.target.checked)} data-testid="sp-bank-ok" /> Accept bank transfer</label>
          <label className="block text-xs sm:col-span-2"><div className="text-[#5C564D] mb-1">Payment notes (optional)</div>
            <input value={p.payment_notes || ""} onChange={(e) => set("payment_notes", e.target.value)} data-testid="sp-notes" placeholder="Any instructions for buyers" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" /></label>
        </div>
      </div>
      <div className="rounded-xl p-3 text-[11px]" style={{ background: "#F2EBE5", color: "#5C564D" }}>
        <strong>Reminder:</strong> Anveshan never collects, holds, processes or settles payments. Buyers will see your direct-payment info only when they choose to pay you directly.
      </div>
      <button type="submit" data-testid="sp-save" className="samaaj-btn-primary">Save seller profile</button>
    </form>
  );
}
